import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, MessageCircle, Shield, Info, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface ChatNotification {
  id: string;
  type: "chat";
  listing_id: string;
  sender_id: string;
  sender_name: string;
  listing_title: string;
  last_message: string;
  unread_count: number;
  last_at: string;
  link: string;
}

interface SystemNotification {
  id: string;
  type: "passport" | "info";
  title: string;
  message: string;
  link: string | null;
  is_read: boolean;
  created_at: string;
}

type AppNotification = ChatNotification | SystemNotification;

const iconMap: Record<string, typeof Bell> = {
  chat: MessageCircle,
  passport: Shield,
  info: Info,
};

export const NotificationBell = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchAll = async () => {
    if (!user) return;

    // Fetch system notifications and chat notifications in parallel
    const [systemResult, chatResult] = await Promise.all([
      supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20),
      supabase
        .from("marketplace_messages")
        .select("listing_id, sender_id, message, created_at")
        .eq("receiver_id", user.id)
        .eq("is_read", false)
        .order("created_at", { ascending: false }),
    ]);

    const allNotifs: AppNotification[] = [];

    // Process system notifications
    if (systemResult.data) {
      for (const n of systemResult.data) {
        allNotifs.push({
          id: n.id,
          type: (n.type === "passport" ? "passport" : "info") as "passport" | "info",
          title: n.title,
          message: n.message,
          link: n.link,
          is_read: n.is_read,
          created_at: n.created_at,
        });
      }
    }

    // Process chat notifications (group by listing+sender)
    const unread = chatResult.data;
    if (unread && unread.length > 0) {
      const groups: Record<string, { listing_id: string; sender_id: string; messages: typeof unread }> = {};
      unread.forEach((m) => {
        const key = `${m.listing_id}_${m.sender_id}`;
        if (!groups[key]) groups[key] = { listing_id: m.listing_id, sender_id: m.sender_id, messages: [] };
        groups[key].messages.push(m);
      });

      const senderIds = [...new Set(unread.map((m) => m.sender_id))];
      const listingIds = [...new Set(unread.map((m) => m.listing_id))];

      const [{ data: profiles }, { data: listings }] = await Promise.all([
        supabase.from("profiles").select("id, first_name, last_name").in("id", senderIds),
        supabase.from("marketplace_listings").select("id, device_passports(brand, model)").in("id", listingIds),
      ]);

      Object.values(groups).forEach((g) => {
        const profile = profiles?.find((p: any) => p.id === g.sender_id);
        const listing = listings?.find((l: any) => l.id === g.listing_id);
        const passport = listing?.device_passports as any;
        allNotifs.push({
          id: `chat_${g.listing_id}_${g.sender_id}`,
          type: "chat",
          listing_id: g.listing_id,
          sender_id: g.sender_id,
          sender_name: `${profile?.first_name || "User"} ${profile?.last_name || ""}`.trim(),
          listing_title: passport ? `${passport.brand} ${passport.model}` : "Listing",
          last_message: g.messages[0].message,
          unread_count: g.messages.length,
          last_at: g.messages[0].created_at,
          link: "/messages",
        });
      });
    }

    // Sort all by date
    allNotifs.sort((a, b) => {
      const dateA = "created_at" in a ? a.created_at : (a as ChatNotification).last_at;
      const dateB = "created_at" in b ? b.created_at : (b as ChatNotification).last_at;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });

    setNotifications(allNotifs);
  };

  useEffect(() => {
    fetchAll();
  }, [user]);

  // Realtime subscriptions
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("notification-bell")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "marketplace_messages" },
        (payload: any) => { if (payload.new.receiver_id === user.id) fetchAll(); })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "marketplace_messages" },
        () => fetchAll())
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications" },
        (payload: any) => { if (payload.new.user_id === user.id) fetchAll(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const markAsRead = async (notifId: string) => {
    await supabase.from("notifications").update({ is_read: true }).eq("id", notifId);
    setNotifications((prev) =>
      prev.map((n) => (n.id === notifId && n.type !== "chat" ? { ...n, is_read: true } as SystemNotification : n))
    );
  };

  const markAllRead = async () => {
    const unreadIds = notifications
      .filter((n) => n.type !== "chat" && !(n as SystemNotification).is_read)
      .map((n) => n.id);
    if (unreadIds.length > 0) {
      await supabase.from("notifications").update({ is_read: true }).in("id", unreadIds);
    }
    setNotifications((prev) =>
      prev.map((n) => (n.type !== "chat" ? { ...n, is_read: true } as SystemNotification : n))
    );
  };

  const totalUnread = notifications.reduce((sum, n) => {
    if (n.type === "chat") return sum + (n as ChatNotification).unread_count;
    return sum + ((n as SystemNotification).is_read ? 0 : 1);
  }, 0);

  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <Button variant="ghost" size="icon" className="relative h-9 w-9" onClick={() => setOpen(!open)}>
        <Bell className="w-5 h-5" />
        {totalUnread > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-[10px] rounded-full flex items-center justify-center font-bold"
          >
            {totalUnread > 99 ? "99+" : totalUnread}
          </motion.span>
        )}
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 z-50 w-[min(20rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] rounded-lg border bg-popover text-popover-foreground shadow-lg"
          >
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-sm">Notifications</h4>
                {totalUnread > 0 && (
                  <p className="text-xs text-muted-foreground">{totalUnread} unread</p>
                )}
              </div>
              {totalUnread > 0 && (
                <Button variant="ghost" size="sm" className="text-xs h-7" onClick={markAllRead}>
                  <Check className="w-3 h-3 mr-1" /> Mark all read
                </Button>
              )}
            </div>
            <ScrollArea className="max-h-[350px]">
              {notifications.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No new notifications
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((n) => {
                    if (n.type === "chat") {
                      const chat = n as ChatNotification;
                      return (
                        <Link
                          key={chat.id}
                          to="/messages"
                          onClick={() => setOpen(false)}
                          className="flex items-start gap-3 p-3 hover:bg-muted/50 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                            <MessageCircle className="w-4 h-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium leading-tight">
                              {chat.sender_name}
                              <span className="font-normal text-muted-foreground"> · {chat.listing_title}</span>
                            </p>
                            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{chat.last_message}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[10px] text-muted-foreground">
                                {new Date(chat.last_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </span>
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive">
                                {chat.unread_count} new
                              </span>
                            </div>
                          </div>
                        </Link>
                      );
                    }

                    const sys = n as SystemNotification;
                    const Icon = iconMap[sys.type] || Info;
                    const isUnread = !sys.is_read;

                    const content = (
                      <div
                        className={cn(
                          "flex items-start gap-3 p-3 hover:bg-muted/50 transition-colors cursor-pointer",
                          isUnread && "bg-primary/5"
                        )}
                        onClick={() => { if (isUnread) markAsRead(sys.id); setOpen(false); }}
                      >
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5",
                          sys.type === "passport" ? "bg-emerald-500/10" : "bg-primary/10"
                        )}>
                          <Icon className={cn("w-4 h-4", sys.type === "passport" ? "text-emerald-500" : "text-primary")} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={cn("text-sm leading-tight", isUnread ? "font-semibold" : "font-medium text-muted-foreground")}>
                            {sys.title}
                          </p>
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{sys.message}</p>
                          <span className="text-[10px] text-muted-foreground mt-1 block">
                            {new Date(sys.created_at).toLocaleDateString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                        {isUnread && <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-2" />}
                      </div>
                    );

                    return sys.link ? (
                      <Link key={sys.id} to={sys.link}>{content}</Link>
                    ) : (
                      <div key={sys.id}>{content}</div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
